/* OZBathroom LED Mirror FAQ stack — natural page-scroll reveal */
(() => {
  'use strict';

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const approach = (value, target, dt, tau, speed) => {
    const distance = target - value;
    const step = clamp(distance * (1 - Math.exp(-dt / tau)), -speed * dt, speed * dt);
    return Math.abs(distance) < 0.001 ? target : value + step;
  };

  class OZBFaqStack extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;

      this.stage = this.querySelector('[data-ofs-stage]');
      this.scrollArea = this.querySelector('[data-ofs-scroll]');
      this.pin = this.querySelector('[data-ofs-pin]');
      this.cards = [...this.querySelectorAll('[data-ofs-card]')];
      if (!this.pin || !this.stage || !this.scrollArea || this.cards.length !== 5) return;

      this.titles = this.cards.map((card) => card.querySelector('[data-ofs-title]'));
      this.bodies = this.cards.map((card) => card.querySelector('[data-ofs-body]'));
      this.covers = this.cards.map((card) => card.querySelector('[data-ofs-cover]'));
      this.footers = this.cards.map((card) => card.querySelector('[data-ofs-reader-footer]'));
      this.live = this.querySelector('[data-ofs-live]');
      if (this.titles.some((item) => !item) || this.bodies.some((item) => !item)) return;

      this.cleanStaleUI();
      this.controller = new AbortController();
      const signal = this.controller.signal;

      this.p = this.target = clamp(Number(this.dataset.initial) || 1, 1, 5);
      this.mode = 'browse';
      this.selected = null;
      this.readPositions = [0, 0, 0, 0, 0];
      this.raf = 0;
      this.scrollFrame = 0;
      this.lastFrame = 0;
      this.lastAnnounced = 0;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');

      this.cards.forEach((card, index) => {
        const list = card.querySelector('[data-ofs-teasers]');
        if (list && !list.children.length) {
          [...this.bodies[index].querySelectorAll('h4')].slice(0, 2).forEach((heading) => {
            const item = document.createElement('li');
            item.textContent = heading.textContent;
            list.append(item);
          });
        }
      });

      this.addEventListener('click', (event) => this.onClick(event), { signal });
      this.addEventListener('keydown', (event) => this.onKey(event), { signal });
      this.bodies.forEach((body) => {
        body.addEventListener('scroll', () => this.updateFadeState(), { passive: true, signal });
      });
      window.addEventListener('scroll', () => this.queueScroll(), { passive: true, signal });
      window.addEventListener('resize', () => {
        this.measure();
        this.updateFromScroll();
        this.render();
      }, { signal });

      this.measure();
      this.classList.add('is-ready');
      this.dataset.readyBuild = 'scroll-flow-20260913';
      this.updateFromScroll();
      this.render();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      cancelAnimationFrame(this.raf);
      cancelAnimationFrame(this.scrollFrame);
      this.raf = 0;
      this.scrollFrame = 0;
    }

    cleanStaleUI() {
      this.querySelectorAll(
        '.ofs__count,.ofs__count-word,.ofs__open-mark,.ofs__cover-label,.ofs__note,.ofs__top,.ofs__nav,.ofs__side-controls,[data-ofs-position],[data-ofs-prev],[data-ofs-next],[data-ofs-linear],[data-ofs-all],[data-ofs-read]'
      ).forEach((element) => element.remove());
      this.querySelectorAll('[data-ofs-back]').forEach((button) => { button.textContent = 'Back to topics'; });
      this.querySelectorAll('[data-ofs-next-topic]').forEach((button) => { button.textContent = 'Next topic'; });
    }

    measure() {
      const styles = getComputedStyle(this);
      this.edge = parseFloat(styles.getPropertyValue('--ofs-edge')) || 56;
      this.coverHeight = parseFloat(styles.getPropertyValue('--ofs-cover-height')) || 330;
      this.stageHeight = this.stage.getBoundingClientRect().height || 574;
      this.readHeight = Math.min(clamp(innerHeight - 120, 430, 720), this.stageHeight);
    }

    updateFadeState() {
      this.covers.forEach((cover) => {
        const visible = !cover.hidden && cover.clientHeight > 0;
        const hasMoreBelow = visible && cover.scrollHeight > cover.clientHeight + 2;
        cover.dataset.ofsFade = hasMoreBelow ? 'on' : 'off';
      });

      this.bodies.forEach((body) => {
        const visible = !body.hidden && body.clientHeight > 0;
        const hasMoreBelow = visible && body.scrollTop + body.clientHeight < body.scrollHeight - 2;
        body.dataset.ofsFade = hasMoreBelow ? 'on' : 'off';
      });
    }

    queueScroll() {
      if (this.mode !== 'browse' || this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = 0;
        this.updateFromScroll();
      });
    }

    updateFromScroll() {
      if (!this.scrollArea || !this.stage || this.mode !== 'browse') return;
      const areaRect = this.scrollArea.getBoundingClientRect();
      const stickyTop = parseFloat(getComputedStyle(this.pin).top) || 0;
      const distance = Math.max(1, this.scrollArea.offsetHeight - this.pin.offsetHeight);
      const progress = clamp((stickyTop - areaRect.top) / distance, 0, 1);
      this.target = 1 + progress * 4;
      this.announce(Math.round(this.target));
      this.wake();
    }

    announce(step) {
      if (!this.live || this.selected !== null || step === this.lastAnnounced) return;
      this.lastAnnounced = step;
      this.live.textContent = 'FAQ topic ' + String(step).padStart(2, '0') + ' of 05';
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (button && this.contains(button)) {
        if (button.matches('[data-ofs-back]')) { this.closeTopic(); return; }
        if (button.matches('[data-ofs-next-topic]')) {
          this.openTopic(((this.selected ?? 0) + 1) % 5);
          return;
        }
        if (button.matches('[data-ofs-title]')) {
          const index = this.cards.indexOf(button.closest('[data-ofs-card]'));
          if (index >= 0) this.openTopic(index);
        }
        return;
      }

      if (this.mode !== 'browse') return;
      const cover = event.target.closest('[data-ofs-cover]');
      if (!cover || !this.contains(cover)) return;
      const index = this.cards.indexOf(cover.closest('[data-ofs-card]'));
      if (index >= 0) this.openTopic(index);
    }

    onKey(event) {
      if (event.key === 'Escape' && this.mode === 'reading') {
        event.preventDefault();
        this.closeTopic();
      }
    }

    openTopic(index) {
      index = clamp(index, 0, 4);
      if (this.mode === 'reading' && this.selected === index) {
        this.closeTopic();
        return;
      }

      if (this.selected !== null) {
        this.readPositions[this.selected] = this.bodies[this.selected].scrollTop;
      }
      this.selected = index;
      this.mode = 'reading';
      this.classList.add('is-reading');
      this.bodies[index].scrollTop = this.readPositions[index];
      this.render();
      this.live && (this.live.textContent = 'Reading ' + this.titles[index].textContent.trim());
      this.bodies[index].focus({ preventScroll: true });
    }

    closeTopic() {
      if (this.selected === null) return;
      const oldIndex = this.selected;
      this.readPositions[oldIndex] = this.bodies[oldIndex].scrollTop;
      this.selected = null;
      this.mode = 'browse';
      this.classList.remove('is-reading');
      this.measure();
      this.updateFromScroll();
      this.render();
      this.titles[oldIndex]?.focus({ preventScroll: true });
    }

    wake() {
      if (!this.raf && this.isConnected) {
        this.lastFrame = 0;
        this.raf = requestAnimationFrame((time) => this.frame(time));
      }
    }

    frame(now) {
      this.raf = 0;
      const dt = this.lastFrame ? Math.min(0.04, (now - this.lastFrame) / 1000) : 1 / 60;
      this.lastFrame = now;

      if (this.mode === 'browse') {
        this.p = this.reduced.matches ? this.target : approach(this.p, this.target, dt, 0.16, 3.4);
      }

      this.render();
      if (this.mode === 'browse' && Math.abs(this.p - this.target) > 0.0005) {
        this.raf = requestAnimationFrame((time) => this.frame(time));
      } else {
        this.lastFrame = 0;
      }
    }

    render() {
      if (!this.cards.length) return;
      const progress = this.p;
      const incoming = this.stageHeight + 32;

      this.cards.forEach((card, index) => {
        const enter = clamp(progress - index, 0, 1);
        const stop = index * this.edge;
        const selected = this.selected === index;
        let y = stop + (1 - enter) * (incoming - stop);
        let height = this.coverHeight;
        let opacity = 1;

        if (selected) {
          y = 0;
          height = this.readHeight;
        } else if (this.selected !== null) {
          opacity = 0;
        }

        card.style.transform = 'translate3d(0,' + y.toFixed(2) + 'px,0)';
        card.style.height = height.toFixed(2) + 'px';
        card.style.zIndex = String(selected ? 100 : 30 + index);
        card.style.opacity = String(opacity);
        card.style.visibility = opacity < 0.002 ? 'hidden' : 'visible';
        card.inert = this.selected !== null && !selected;
        card.setAttribute('aria-hidden', this.selected !== null && !selected ? 'true' : 'false');

        const reading = selected;
        this.bodies[index].hidden = !reading;
        this.bodies[index].inert = !reading;
        if (this.footers[index]) this.footers[index].hidden = !reading;
        if (this.covers[index]) this.covers[index].hidden = reading;
        this.titles[index].setAttribute('aria-expanded', String(reading));
        this.titles[index].tabIndex = selected || this.selected === null ? 0 : -1;
      });

      this.updateFadeState();
    }
  }

  if (!customElements.get('ozb-faq-stack')) {
    customElements.define('ozb-faq-stack', OZBFaqStack);
  }
})();
